export default {
  global: {
    brandName: 'Wayke',
    brandWebsiteUrl: 'wayke.se',
    currencyUnit: 'kr',
    modalTitle: 'Value your car in collaboration with {{brandName}}',
  },
  other: {
    loading: 'Loading...',
    closeModal: 'Close modal',
  },
  glossary: {
    change: 'Change',
    next: 'Next',
    modelYear: 'Model Year',
    manufactureYear: 'Manufacturing Year',
    gearbox: 'Gearbox',
    color: 'Color',
    fuelType: 'Fuel group',
    chassis: 'Kaross',
    segment: 'Segment',
    drivingWheel: 'Driving Wheel',
    mileage: 'Miltal',
    mileageUnit_one: 'Mile',
    mileageUnit_other: 'Miles',
    condition: 'Condition',
  },
  error: {
    unspecific: 'An error has occurred. Try again.',
    valuation:
      'Unfortunately, we did not find a match for the registration number you entered. Please check that the registration number is correct.',
    vehicle:
      "Oops, we can't seem to retrieve any data on this car and can't give you a valuation, for example it could be because the car is imported. Please also double check that you have entered the correct registration number.",
  },
  footer: {
    poweredByBrand: 'Powered by {{link}}',
    poweredByBrandLinkTitle: 'Go to {{website}}',
    poweredByBrandLinkUrl: 'https://www.wayke.se',
  },
  input: {
    registrationNumber: {
      label: 'Registration number',
      placeholder: 'Enter registration number',
      error: 'A valid registration number in the format ABC123 or ABC12A must be entered',
    },
    mileage: {
      label: 'Miltal',
      placeholder: "Enter the car's mileage",
      error: 'A valid mileage number must be entered',
    },
    firstName: {
      label: 'First name',
      placeholder: 'Enter first name',
      error: 'You must enter your first name',
    },
    lastName: {
      label: 'Surname',
      placeholder: 'Enter last name',
      error: 'You must enter your last name',
    },
    email: {
      label: 'Email address',
      placeholder: 'Enter email address',
      error: 'You must enter your email address',
    },
    phone: {
      label: 'Phone number (optional)',
      placeholder: 'Enter phone number',
      error: 'You must enter your phone number',
    },
    branch: {
      label: 'Plant',
      placeholder: 'Select facility',
      help: 'We have several facilities in Sweden. Select the facility you want to contact.',
    },
    description: {
      label: 'Good to know about my car (optional)',
      placeholders:
        'Is there anything else about your car that you want to tell us? Something that can affect the value such as service history, extra equipment, winter tires?',
      error: 'You must add a description',
    },
  },
  init: {
    title: 'Free valuation of your car',
    expandedTitle: 'Evaluate the car for free without committing to anything',
    startButtonText: 'Start Valuation',
    expandButtonText: "Expand {{brandName}}'s rating module",
    minifyButtonText: "Minify {{brandName}}'s rating module",
    association: {
      body: 'Evaluation in collaboration with {{link}}',
      linkTitle: 'Go to {{website}}',
      linkUrl: 'https://www.wayke.se',
    },
  },
  summary: {
    thankYouMessage: 'Thank {{name}} for your interest!',
    whatHappensNow: {
      heading: 'What happens now?',
      body: [
        'We will review the details of your car and contact you, usually within 24 hours. After that, we go through all the details together and if you choose to proceed with the sale, we will help you with all the paperwork.',
        'If you are interested in buying a new car, we can help you with that too.',
      ],
    },
    closeButtonText: 'Close',
  },
  confirmClose: {
    heading: 'Finish the valuation',
    body: 'Are you sure you want to end the evaluation?',
    buttonContinueText: 'Continue with my rating',
    buttonCancelText: 'Cancel my rating',
  },
  vehicleDetails: {
    label: 'Bulip tasks',
  },
  mileageAndCondition: {
    label: "The car's mileage and condition",
    heading: "State the car's mileage and condition",
    conditionRadioGroup: {
      label: 'Car condition',
      error: 'Must select car condition',
      help: 'Choose the option that best suits the condition of the car. If you want to sell the car, in the next step you will also be able to write in yourself if there is something you think we need to know about your car.',
    },
    seeEstimatedPurchasePrice: 'See estimated purchase price',
    condition: {
      veryGood: {
        label: 'Very good condition',
        requirements: [
          'No scratches or damage',
          'Serviced on every occasion with stamps in the service book',
          'Tires with very good tread depth (5-8 mm)',
        ],
      },
      good: {
        label: 'Good condition',
        requirements: [
          'Some minor scratches and/or damage',
          'Serviced on every occasion with stamps in the service book',
          'Tyres that do not need to be replaced (tread depth of 3-5 mm)',
        ],
      },
      ok: {
        label: 'Perfect condition',
        requirements: [
          'There are some scratches and damages',
          'Not serviced on every occasion',
          'Tyre in need of replacement (tread depth below 3 mm)',
        ],
      },
    },
  },
  valuation: {
    label: 'Valuation',
    estimatedPurchasePrice: {
      heading: 'Estimated purchase price',
      body: 'A range between {{low}} and {{high}}.',
    },
    calculation: {
      heading: 'How did we calculate this?',
      body: [
        'The purchase price is based on similar cars on the market that are currently for sale or have recently been for sale.',
        'We include a lot of parameters about your car in order to give the most accurate price possible, where our service is constantly being developed and improved.',
        "The purchase price you get here is an indication of the car's value, but the final price can go up and down depending on the car's condition and other factors we could not take into account in this valuation. In order to give a final purchase price, we need to see your car and its service history.",
        "If there is too little information on the market about your particular car, it can be difficult to give a purchase price. Then feel free to contact us and we'll take a closer look at your car in the garage instead.",
      ],
    },
    factors: {
      label: 'Factors that can affect the selling price',
      positive: {
        heading: 'Factors that can positively affect the selling price can be:',
        usps: [
          "If your car has any value-enhancing equipment that we can't see yet, such as a retrofitted towbar or new rims.",
          'If your car is in very good condition and you have taken extra good care of it.',
          'If there is currently a high demand for your model.',
        ],
      },
      negative: {
        heading: 'Factors that can negatively affect the selling price can be:',
        usps: [
          'If there is damage or defects that require us to make repairs before resale.',
          "If there is something in your car's history that does not appear until we have checked the car, for example if there are missing service stamps.",
          'If there are many similar cars for sale right now.',
        ],
      },
    },
  },
  about: {
    label: 'Information about how the valuation works',
    heading: 'Value the car for free',
    body: [
      "With your registration number, we have retrieved several pieces of information about your car. Just enter the car's mileage and condition and you will receive a free estimated purchase price of your car.",
      'If you also want to sell the car, you can simply fill in your contact details and we will get back to you.',
    ],
    checklist: [
      'The assessment is completely free of charge',
      "You decide for yourself if you want to sell the car - and don't commit to anything",
    ],
  },
  contact: {
    label: 'Declaration of interest sale of car',
    heading: 'Do you want to sell the car?',
    body: 'Sell us your car! Fill in the expression of interest and we will contact you. You are not committing to anything by registering interest.',
    detailsHeading: 'Contact Details',
    carInformationHeading: 'Do you want to provide any more information about your car?',
    nextStep: {
      heading: "What's the next step?",
      body: 'We will contact you to sort out the details of your sale together. Remember you are not committing to anything.',
    },
    requestError: 'It was not possible to send the expression of interest. Try again.',
    disclaimer: {
      body: 'Your registration number and personal data are processed by our valuation supplier {{brandName}} and will be shared with {{branch}}. {{link}}',
      link: {
        title: 'Read more about how your registration number and personal data are processed',
        text: 'Read more',
        url: 'https://www.wayke.se/personuppgiftspolicy-wayke',
      },
    },
    sendFormButtonText: 'Submit expression of interest',
  },
};
